// src/home/insuranceDates.js — the customer's own upcoming crop-insurance dates.
//
// SOURCED, NOT TYPED. Every date comes from RMA's own ADM, loaded into
// `rma_dates` (sql/086, api/_rma-dates.js), and is matched to the exact
// state x county x commodity the grower insures — read from policy_fields,
// which carries state_fips + county_fips on every insured field. Nothing here
// knows a date of its own, so nothing here can drift when RMA moves one.
//
// The Home page of this app once declined to show RMA deadlines at all
// (2026-08-22: "if you cant automate RMA deadlines, then leave it out") because
// no sourced table existed. sql/086 is that table; this is the automation.
//
// Rules, each of which fails quietly the other way:
//  - BASE PLANS ONLY (01 / 02 / 03 / 90). Area and overlay plans carry their own
//    rows; a grower's deadline is the base policy's.
//  - A PLACE MUST MATCH ON ALL THREE KEYS. Sixteen county names exist in both NY
//    and PA; a name join would hand one state's date to the other.
//  - WHERE A GROWER'S COUNTIES DISAGREE the EARLIEST date is shown and flagged —
//    a deadline met in the earliest county is met in all of them.
//  - A date RMA has not published is ABSENT, never guessed from last year.
//
// Pure: no fetch, no React. The caller supplies the rows.

export const BASE_PLAN_CODES = new Set(["01", "02", "03", "90"]);

export const INSURANCE_DATE_KINDS = [
  { col: "sales_closing_date",     label: "sales closing", sub: "last day to buy or change coverage" },
  { col: "acreage_reporting_date", label: "acreage report due", sub: "report planted acres to your agent" },
  { col: "final_planting_date",    label: "final planting date", sub: "last day to plant for full coverage" },
];

const isoOf = (d) => {
  const t = d instanceof Date ? d : new Date(d);
  return t.getFullYear() + "-" + String(t.getMonth() + 1).padStart(2, "0") + "-" + String(t.getDate()).padStart(2, "0");
};
const daysBetween = (a, b) => Math.round((Date.parse(b + "T12:00:00Z") - Date.parse(a + "T12:00:00Z")) / 86400000);

// places: [{ state_fips, county_fips, rma_crop_code }]
// dates:  rma_dates rows
// cropName: { "0011": "Wheat", ... }
export function upcomingInsuranceDates({ places = [], dates = [], today = new Date(), horizonDays = 200, cropName = {} } = {}) {
  const todayIso = typeof today === "string" ? today.slice(0, 10) : isoOf(today);
  const placeSet = new Set();
  for (const p of places || []) {
    const s = String(p.state_fips || "").padStart(2, "0"), c = String(p.county_fips || "").padStart(3, "0");
    const k = String(p.rma_crop_code || "").padStart(4, "0");
    if (s !== "00" && c !== "000" && k !== "0000") placeSet.add(s + "|" + c + "|" + k);
  }
  if (!placeSet.size) return [];

  const buckets = new Map();   // kind|commodity|crop_year -> { dates:Set, counties:Set }
  for (const r of dates || []) {
    if (!BASE_PLAN_CODES.has(String(r.insurance_plan_code || ""))) continue;
    const key = String(r.state_code || "") + "|" + String(r.county_code || "") + "|" + String(r.commodity_code || "");
    if (!placeSet.has(key)) continue;
    for (const k of INSURANCE_DATE_KINDS) {
      const d = r[k.col] ? String(r[k.col]).slice(0, 10) : null;
      if (!d) continue;
      const away = daysBetween(todayIso, d);
      if (away < 0 || away > horizonDays) continue;
      const bk = k.col + "|" + r.commodity_code + "|" + r.crop_year;
      const b = buckets.get(bk) || { kind: k, commodity: r.commodity_code, cropYear: r.crop_year, dates: new Set(), counties: new Set() };
      b.dates.add(d);
      b.counties.add(String(r.state_code) + String(r.county_code));
      buckets.set(bk, b);
    }
  }

  const out = [];
  for (const b of buckets.values()) {
    const sorted = [...b.dates].sort();
    const date = sorted[0];
    const crop = cropName[b.commodity] || ("commodity " + b.commodity);
    out.push({
      id: "ins-" + b.kind.col + "-" + b.commodity + "-" + b.cropYear,
      kind: b.kind.col,
      date,
      daysAway: daysBetween(todayIso, date),
      title: crop + " " + b.kind.label,
      crop,
      cropYear: b.cropYear,
      sub: b.cropYear + " crop · " + b.kind.sub,
      counties: b.counties.size,
      varies: sorted.length > 1,
      source: "RMA actuarial data",
    });
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.title < b.title ? -1 : 1));
}
